import { cn } from '../../lib/utils'

export interface TabItem<K extends string | number> {
  key: K
  label: string
  icon?: string
  count?: number
}

export function Tabs<K extends string | number>({
  tabs,
  active,
  onChange,
  className,
}: {
  tabs: TabItem<K>[]
  active: K
  onChange: (key: K) => void
  className?: string
}) {
  return (
    <div className={cn('flex gap-1 overflow-x-auto border-b border-border/70', className)} role="tablist">
      {tabs.map((t) => {
        const isActive = t.key === active
        return (
          <button
            key={String(t.key)}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.key)}
            className={cn(
              '-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-13 font-medium transition-colors',
              isActive ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-white',
            )}
          >
            {t.icon && <i className={cn(t.icon, 'text-sm')} aria-hidden />}
            {t.label}
            {t.count !== undefined && (
              <span className="rounded bg-expense px-1.5 text-2xs font-semibold text-white">{t.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function Pagination({
  page,
  totalPages,
  onChange,
  summary,
}: {
  page: number
  totalPages: number
  onChange: (page: number) => void
  summary?: string
}) {
  if (totalPages <= 1 && !summary) return null

  const pages: number[] = []
  const start = Math.max(1, Math.min(page - 2, totalPages - 4))
  for (let p = start; p <= Math.min(totalPages, start + 4); p++) pages.push(p)

  const base = 'flex h-8 min-w-8 items-center justify-center rounded px-2.5 text-13 transition-colors disabled:opacity-40 disabled:pointer-events-none'

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <p className="text-13 text-muted">{summary}</p>
      {totalPages > 1 && (
        <nav className="flex items-center gap-1" aria-label="Paginação">
          <button type="button" className={cn(base, 'bg-surface2 text-muted hover:text-white')} disabled={page <= 1} onClick={() => onChange(page - 1)}>
            Anterior
          </button>
          {pages.map((p) => (
            <button
              key={p}
              type="button"
              aria-current={p === page ? 'page' : undefined}
              className={cn(base, p === page ? 'bg-primary font-semibold text-bg' : 'bg-surface2 text-muted hover:text-white')}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          ))}
          <button type="button" className={cn(base, 'bg-surface2 text-muted hover:text-white')} disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
            Próxima
          </button>
        </nav>
      )}
    </div>
  )
}

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

export function MonthNavigator({ mes, ano, onChange }: { mes: number; ano: number; onChange: (mes: number, ano: number) => void }) {
  function shift(delta: number) {
    const d = new Date(ano, mes - 1 + delta, 1)
    onChange(d.getMonth() + 1, d.getFullYear())
  }
  return (
    <div className="inline-flex h-[38px] items-center overflow-hidden rounded bg-surface2">
      <button type="button" onClick={() => shift(-1)} className="flex h-full w-9 items-center justify-center text-muted hover:bg-surface3 hover:text-white" aria-label="Mês anterior">
        <i className="ri-arrow-left-s-line text-lg" aria-hidden />
      </button>
      <span className="flex min-w-[140px] items-center justify-center gap-1.5 px-2 text-13 font-medium text-white">
        <i className="ri-calendar-2-line text-muted" aria-hidden />
        {MESES[mes - 1]} {ano}
      </span>
      <button type="button" onClick={() => shift(1)} className="flex h-full w-9 items-center justify-center text-muted hover:bg-surface3 hover:text-white" aria-label="Próximo mês">
        <i className="ri-arrow-right-s-line text-lg" aria-hidden />
      </button>
    </div>
  )
}
